import React, { useState, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { TextField } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import PlacementItemCard from './PlacementItemCard';

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
  },
  searchWrap: {
    padding: '8px 12px 4px',
    flexShrink: 0,
  },
  searchBox: {
    '& .MuiOutlinedInput-root': { fontSize: 13 },
    '& .MuiOutlinedInput-input': { padding: '7px 8px' },
  },
  list: {
    flex: 1,
    overflowY: 'auto',
    padding: '4px 12px 8px',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 4px',
    cursor: 'pointer',
    userSelect: 'none',
    borderRadius: 6,
    marginBottom: 4,
    '&:hover': { background: '#f5f5f5' },
  },
  sectionDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    flexShrink: 0,
  },
  sectionLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: '#555',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    flex: 1,
  },
  sectionMeta: {
    fontSize: 11,
    color: '#999',
  },
  sectionChevron: {
    fontSize: 10,
    color: '#bbb',
    width: 10,
  },
  sectionItems: {
    marginBottom: 8,
  },
  empty: {
    padding: 24,
    textAlign: 'center',
    color: '#aaa',
    fontSize: 13,
  },
}));

/**
 * PlacementQueue — grouped, searchable list of plan items awaiting placement.
 *
 * Props:
 *   groupedItems   – array of { section, color, items[], placedCount, totalCount }
 *   activeItemId   – currently selected item id
 *   onSelectItem   – (itemId) => void
 *   onDrawItem     – (itemId, reset?) => void
 *   onSkipItem     – (itemId) => void
 *   onChangeType   – (itemId, newType) => void
 */
const PlacementQueue = ({
  groupedItems = [],
  activeItemId,
  activePlacementId,
  onSelectItem,
  onSkipItem,
  onChangeType,
  cropBaseUrl,
  onPickSymbol,
  onFindOnPage,
  expandedItems,
  onToggleExpand,
  onEditPlacement,
  onDeletePlacement,
  onAddPlacement,
  onConfirmEdit,
  onCancelEdit,
  editingPlacement,
}) => {
  const classes = useStyles();
  const [searchQuery, setSearchQuery] = useState('');
  const [collapsedSections, setCollapsedSections] = useState({});

  const toggleSection = (section) => {
    setCollapsedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Filter items across all groups by search query
  const filteredGroups = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return groupedItems;

    return groupedItems
      .map(group => ({
        ...group,
        items: group.items.filter(item => {
          const desc = (item.description || '').toLowerCase();
          const section = (item.section || '').toLowerCase();
          const botanical = (item.botanicalName || '').toLowerCase();
          return desc.includes(q) || section.includes(q) || botanical.includes(q);
        }),
      }))
      .filter(group => group.items.length > 0);
  }, [groupedItems, searchQuery]);

  const totalItems = groupedItems.reduce((sum, g) => sum + g.items.length, 0);

  return (
    <div className={classes.root}>
      {/* Search bar — shown when there are 3+ items */}
      {totalItems >= 3 && (
        <div className={classes.searchWrap}>
          <TextField
            className={classes.searchBox}
            size="small"
            variant="outlined"
            fullWidth
            placeholder="Search items..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <SearchIcon style={{ fontSize: 16, color: '#bbb', marginRight: 4 }} />
              ),
            }}
          />
        </div>
      )}

      <div className={classes.list}>
        {filteredGroups.length === 0 && (
          <div className={classes.empty}>
            {searchQuery ? 'No items match your search.' : 'No items in queue.'}
          </div>
        )}

        {filteredGroups.map(group => {
          const isCollapsed = !!collapsedSections[group.section];
          const { placedCount, totalCount } = group;

          return (
            <div key={group.section}>
              {/* Section header */}
              <div
                className={classes.sectionHeader}
                onClick={() => toggleSection(group.section)}
              >
                <span className={classes.sectionChevron}>
                  {isCollapsed ? '▸' : '▾'}
                </span>
                <div className={classes.sectionDot} style={{ background: group.color }} />
                <span className={classes.sectionLabel}>{group.section}</span>
                <span className={classes.sectionMeta}>
                  {placedCount}/{totalCount}
                </span>
              </div>

              {/* Items */}
              {!isCollapsed && (
                <div className={classes.sectionItems}>
                  {group.items.map(item => (
                    <PlacementItemCard
                      key={item.itemId}
                      item={item}
                      isActive={item.itemId === activeItemId}
                      isExpanded={expandedItems && expandedItems.has(item.itemId)}
                      activePlacementId={activePlacementId}
                      onSelect={onSelectItem}
                      onToggleExpand={onToggleExpand}
                      onSkip={onSkipItem}
                      onChangeType={onChangeType}
                      cropBaseUrl={cropBaseUrl}
                      onPickSymbol={onPickSymbol}
                      onFindOnPage={onFindOnPage}
                      onEditPlacement={onEditPlacement}
                      onDeletePlacement={onDeletePlacement}
                      onAddPlacement={onAddPlacement}
                      onConfirmEdit={onConfirmEdit}
                      onCancelEdit={onCancelEdit}
                      editingPlacement={editingPlacement}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PlacementQueue;
